import { G } from '@mobily/ts-belt';
import { isValid, parse } from 'date-fns';
import { match } from 'ts-pattern';
import { z } from 'zod';

import { ZodCurrency } from '@Common/types/Amount';
import { DOMAIN_REGEX } from '@Common/types/bo/domains';
import { ZodFileUploadInput } from '@Common/types/bo/file_upload';
import { ZodReviewedRequestState } from '@Common/types/bo/reviewed_requests';
import { buildZodBoListQueryParsed } from '@Common/types/bo/utils/build_zod_bo_list_query';
import { ZodDate } from '@Common/types/date';
import { ZodCompanyDirector } from '@Common/types/questions/company';
import { ZodSearchCompanyItem } from '@Common/types/questions/search_company';
import { ZodIDPathParam, ZodId } from '@Common/types/request';
import {
  ZodBankAccountBic,
  ZodBankAccountIban,
  ZodIntlAccountNumber,
  ZodNonSepaIntlBic,
  ZodUkAccountNumber,
  ZodUkSortCode,
} from '@Common/types/zod/bank_account';
import { createZodArrayable } from '@Common/types/zod/zod-arrayable';
import { ZodCountryCode } from '@Common/types/zod/zod-country-code';
import { ZodSecureEmail } from '@Common/types/zod/zod-email';
import {
  // @ts-expect-error used once compiled by TSC, which significantly improves performance
  type EndpointConfig,
  endpoint,
  type GetEndpointType,
} from '@Common/types/zod/zod-endpoint';
import { createZodParamsValidator } from '@Common/types/zod/zod-params';
import { ZodPhoneNumber } from '@Common/types/zod/zod-phone-number';
import { ZodTrimmedString } from '@Common/types/zod/zod-trimmed-string';

import type { BoListQueryBase } from '@Common/types/bo/bo_list_query';

export const ZodYearlyDate = z
  .string()
  .regex(/^\d{2}\/\d{2}$/) // dd/MM
  .refine(
    (start) => isValid(parse(`${start}/2023`, 'dd/MM/yyyy', new Date())), // Non leap year
    {
      message: 'Date must follow dd/MM format (ex. 31/12)',
    },
  );

export const ZodDomain = z.string().regex(DOMAIN_REGEX);

export const ZodDomainsList = ZodDomain.array().min(1);

export const KybStatusValues = ['created', 'in_review', 'verified', 'denied', 'resigned'] as const;
export const ZodKybStatus = z.enum(KybStatusValues);

export type KybStatus = z.TypeOf<typeof ZodKybStatus>;

export const ZodGetCompanyContact = z.object({
  id: ZodId,
  firstname: ZodTrimmedString,
  lastname: ZodTrimmedString,
  email: ZodSecureEmail,
  email_state: z.enum(['blocked', 'permanently_blocked', 'invalid']).nullable(),
  landline: ZodPhoneNumber.nullable().optional(),
  phone_number: ZodPhoneNumber.nullable().optional(),
  role: ZodTrimmedString,
  is_legal_representative: z.boolean(),
  is_business_correspondent: z.boolean(),
  is_migrated: z.boolean(),
});

export type GetCompanyContact = z.infer<typeof ZodGetCompanyContact>;

export const B2bCompanyStatusValues = ['client', 'client_inactive', 'client_left'] as const;
export const ZodB2bCompanyStatus = z.enum(B2bCompanyStatusValues);

export type B2bCompanyStatus = z.TypeOf<typeof ZodB2bCompanyStatus>;

export const B2bCompanyInactiveReasonValues = [
  'no_outstanding_balance',
  'inactive_over_5_years',
  'transfer_merger_split',
  'judicial_liquidation',
  'judicial_recovery',
  'business_closure',
  'protective_measure',
  'deregistered_client',
] as const;
export const ZodB2bCompanyInactiveReason = z.enum(B2bCompanyInactiveReasonValues);
export type B2bCompanyInactiveReason = z.TypeOf<typeof ZodB2bCompanyInactiveReason>;

export const B2bCompanyLegalFormValues = [
  'AGCE',
  'ASSOC',
  'AT',
  'BQUE',
  'CAB',
  'CIE',
  'COPRO',
  'COOP',
  'CTRE',
  'CUMA',
  'DIR',
  'EARL',
  'EI',
  'EIRL',
  'EURL',
  'ENTR',
  'ETS',
  'FED',
  'GAEC',
  'GFA',
  'GIE',
  'GIEIE',
  'GPE',
  'GPT',
  'HRS',
  'SA',
  'SARL',
  'SAS',
  'SASU',
  'SC',
  'SCA',
  'SCE',
  'SCEA',
  'SCEV',
  'SCI',
  'SCM',
  'SCP',
  'SCPI',
  'SDF',
  'SELAR',
  'SELAS',
  'SELRL',
  'SEM',
  'SIVOM',
  'SIVOS',
  'SNC',
  'SNE',
  'SPFPL',
  'STE',
  'SUCC',
  'SYND',
  'TP',
  'SEP',
  'other',
] as const;
export const ZodB2bCompanyLegalForm = z.enum(B2bCompanyLegalFormValues);
export type B2bCompanyLegalForm = z.TypeOf<typeof ZodB2bCompanyLegalForm>;

const ZodB2bCompanyFlag = z.enum(['vip']);

export type B2bCompanyFlag = z.TypeOf<typeof ZodB2bCompanyFlag>;

export const ZodB2bCompaniesFlags = ZodB2bCompanyFlag.array().refine(
  (items) => new Set(items).size === items.length,
  {
    message: 'Flags must be unique',
  },
);

export const ZodB2bCompanyDetails = z.object({
  id: ZodId,
  name: ZodTrimmedString,
  siret: ZodTrimmedString.length(14).nullable(),
  international_identifier: ZodTrimmedString.nullable(),
  registration_date: z.date().nullable(),
  created_at: z.date(),
  updated_at: z.date(),
  logo_url: ZodTrimmedString.nullable(),
  start_fiscal_year: ZodTrimmedString,
  naf_code: ZodTrimmedString.nullable(),
  legal_form: ZodB2bCompanyLegalForm.nullable(),
  rcs_location: ZodTrimmedString.nullable(),
  headcount: z.number().int().nonnegative().nullable(),
  previous_naf_code: ZodTrimmedString.nullable(),
  collective_agreement_code: ZodTrimmedString.nullable(),
  collective_agreement_name: ZodTrimmedString.nullable(),
  kyb_status: ZodKybStatus,
  mdlr_customer_id: ZodTrimmedString.nullable(),
  mdlr_customer_url: ZodTrimmedString.url().nullable(),
  mdlr_account_id: ZodTrimmedString.nullable(),
  mdlr_account_iban: ZodTrimmedString.nullable(),
  payment_reference: ZodTrimmedString.nullable(),
  formance_ledger_url: ZodTrimmedString.url().nullable(),
  domains: ZodDomain.array(),
  has_documents: z.boolean(),
  has_admins: z.boolean(),
  has_contracts: z.boolean(),
  employee_cnt: z.number(),
  bnmf_tasks_cnt: z.number(),
  parent_company_id: ZodId.nullable(),
  street: ZodTrimmedString.nullable(),
  postalcode: ZodTrimmedString.nullable(),
  city: ZodTrimmedString.nullable(),
  country: ZodCountryCode,
  contacts: z.array(ZodGetCompanyContact),
  mailing_street: ZodTrimmedString.nullable(),
  mailing_postalcode: ZodTrimmedString.nullable(),
  mailing_city: ZodTrimmedString.nullable(),
  mailing_country: ZodCountryCode.nullable(),
  billing_street: ZodTrimmedString.nullable(),
  billing_postalcode: ZodTrimmedString.nullable(),
  billing_city: ZodTrimmedString.nullable(),
  billing_country: ZodCountryCode.nullable(),
  status: ZodB2bCompanyStatus,
  inactive_reason: ZodB2bCompanyInactiveReason.nullable(),
  flags: ZodB2bCompaniesFlags,
  is_international: z.boolean(),
  has_indirect_management_contract: z.boolean(),
  has_payment_methods: z.boolean(),
  has_ccb: z.boolean(),
  retirement_age: z.number().int().min(55).max(70).nullable(),
  external_code_enterprise: z.number().int(),
  is_confidential: z.boolean(),
});

export const ZodGetB2bCompaniesOneResponse = z.object({
  id: ZodId,
  name: ZodTrimmedString,
  siret: ZodTrimmedString.nullable(),
  international_identifier: ZodTrimmedString.nullable(),
  registration_date: z.date().nullable(),
  address: ZodTrimmedString,
  created_at: z.date(),
  updated_at: z.date(),
  start_fiscal_year: ZodTrimmedString,
  naf_code: ZodTrimmedString.nullable(),
  collective_agreement_code: ZodTrimmedString.nullable(),
  collective_agreement_name: ZodTrimmedString.nullable(),
  kyb_status: ZodKybStatus,
  parent_company_id: ZodId.nullable(),
  flags: ZodB2bCompaniesFlags,
  is_international: z.boolean(),
  has_indirect_management_contract: z.boolean(),
  external_code_enterprise: z.number().int(),
});

export type GetB2bCompaniesOneResponse = z.TypeOf<typeof ZodGetB2bCompaniesOneResponse>;
export type GetB2bCompanyDetailsResponse = z.TypeOf<typeof ZodB2bCompanyDetails>;

const ZodGetB2bCompaniesResponse = ZodGetB2bCompaniesOneResponse.array();
export type GetB2bCompaniesResponse = z.TypeOf<typeof ZodGetB2bCompaniesResponse>;

export const getB2bCompaniesSortColumns: (keyof GetB2bCompaniesOneResponse)[] = [
  'id',
  'name',
  'siret',
  'created_at',
  'updated_at',
  'start_fiscal_year',
  'naf_code',
  'collective_agreement_code',
  'external_code_enterprise',
  'collective_agreement_name',
  'kyb_status',
  'parent_company_id',
];

export const ZodGetB2bCompaniesQueryFilters = z
  .object({
    ...ZodGetB2bCompaniesOneResponse.pick({
      address: true,
      start_fiscal_year: true,
    }).shape,
    id: createZodArrayable(ZodId),
    name: createZodArrayable(ZodTrimmedString),
    siret: createZodArrayable(ZodTrimmedString),
    naf_code: createZodArrayable(ZodTrimmedString),
    external_code_enterprise: createZodArrayable(ZodId),
    collective_agreement_code: createZodArrayable(ZodTrimmedString),
    collective_agreement_name: createZodArrayable(ZodTrimmedString),
    kyb_status: createZodArrayable(ZodKybStatus),
    parent_company_id: createZodArrayable(ZodId),
    from_created_at: ZodTrimmedString,
    to_created_at: ZodTrimmedString,
    registration_date: ZodTrimmedString,
    flags: ZodB2bCompanyFlag,
    international_identifier: createZodArrayable(ZodTrimmedString),
    is_international: z.boolean(),
    has_indirect_management_contract: z.boolean(),
  })
  .partial();

const ZodGetB2bCompaniesQuery = buildZodBoListQueryParsed(
  ZodGetB2bCompaniesQueryFilters,
  getB2bCompaniesSortColumns,
  { range: [0, 100] },
);

export type GetB2bCompaniesQuery = BoListQueryBase<
  z.TypeOf<typeof ZodGetB2bCompaniesQueryFilters>,
  (typeof getB2bCompaniesSortColumns)[number]
>;

export const B2bCompanyDocumentTypeValues = [
  'other',
  'sepa_mandate_rib',
  'b2b_admin_welcome_letter',
  'b2b_admin_third_party_mandate',
  'b2b_business_sepa_mandate_form_signed',
  'b2b_business_sepa_mandate_form_unsigned',
  'campaign_operation_summary',
  'campaign_fund_request',
  'in_transfer_report',
  'out_transfer_report',
  'transfer_in_termination_letter',
  'transfer_out_termination_letter',
  'similar_contract_certificate_document',
  'employee_positions_list',
  'employee_data_fress',
  'company_rib_document',
  'employees_bulk_create',
  'csg_crds_monthly_summary',
  'csg_crds_annual_summary',
  'b2b_pricing',
  'correction_summary',
  'matching_contribution_fund_request',
] as const;

export const ZodB2bCompanyDocumentType = z.enum(B2bCompanyDocumentTypeValues);

export type B2bCompanyDocumentType = z.TypeOf<typeof ZodB2bCompanyDocumentType>;

export const B2bCompanyDocumentCategoryValues = [
  'company',
  'contracts',
  'operations',
  'certificates',
  'commercial_supports',
  'invoices',
] as const;

export const ZodB2bCompanyDocumentCategory = z.enum(B2bCompanyDocumentCategoryValues);

export type B2bCompanyDocumentCategory = (typeof B2bCompanyDocumentCategoryValues)[number];

export const getDocumentCategory = (type: B2bCompanyDocumentType): B2bCompanyDocumentCategory =>
  match(type)
    .with('company_rib_document', () => 'company' as const)
    .with('sepa_mandate_rib', () => 'company' as const)
    .with('b2b_admin_welcome_letter', () => 'company' as const)
    .with('employees_bulk_create', () => 'company' as const)
    .with('employee_positions_list', () => 'company' as const)
    .with('employee_data_fress', () => 'company' as const)
    .with('b2b_business_sepa_mandate_form_signed', () => 'company' as const)
    .with('b2b_business_sepa_mandate_form_unsigned', () => 'company' as const)
    .with('correction_summary', () => 'company' as const)
    .with('csg_crds_monthly_summary', () => 'certificates' as const)
    .with('csg_crds_annual_summary', () => 'certificates' as const)
    .with('other', () => 'company' as const)
    .with('similar_contract_certificate_document', () => 'contracts' as const)
    .with('b2b_admin_third_party_mandate', () => 'contracts' as const)
    .with('campaign_operation_summary', () => 'operations' as const)
    .with('campaign_fund_request', () => 'operations' as const)
    .with('matching_contribution_fund_request', () => 'operations' as const)
    .with('in_transfer_report', () => 'operations' as const)
    .with('out_transfer_report', () => 'operations' as const)
    .with('transfer_in_termination_letter', () => 'operations' as const)
    .with('transfer_out_termination_letter', () => 'operations' as const)
    .with('b2b_pricing', () => 'commercial_supports' as const)
    .exhaustive();

export const getTypesForCategory = (
  category: B2bCompanyDocumentCategory,
): B2bCompanyDocumentType[] =>
  B2bCompanyDocumentTypeValues.filter((type) => getDocumentCategory(type) === category);

export const categoryHasTypes = (category: B2bCompanyDocumentCategory): boolean =>
  getTypesForCategory(category).length > 0;

const ZodB2bPostCompanyDocumentRequest = z.object({
  company_id: ZodId,
  presigned_path: ZodTrimmedString,
  document_name: ZodTrimmedString,
  document_type: ZodB2bCompanyDocumentType,
  task_id: ZodId.nullish(),
  document_comment: ZodTrimmedString.nullable(),
});

export type PostB2bCompanyDocumentRequest = z.TypeOf<typeof ZodB2bPostCompanyDocumentRequest>;

export const B2bCompanyDocumentSourceValues = ['bo', 'bo_external'] as const;
export type B2bCompanyDocumentSource = z.TypeOf<typeof ZodB2bCompanyDocumentSource>;

export const ZodB2bCompanyDocumentSource = z.enum(B2bCompanyDocumentSourceValues);
export const ZodB2bCompanyDocumentDetails = z.object({
  id: ZodId,
  company_id: ZodId,
  type: ZodB2bCompanyDocumentType,
  document_url: ZodTrimmedString,
  document_name: ZodTrimmedString,
  document_comment: ZodTrimmedString.nullable(),
  is_archived: z.boolean(),
  is_generated: z.boolean(),
  task_id: ZodId.nullable(),
  b2b_campaign_id: ZodId.nullable(),
  savings_funds_request_id: ZodId.nullable(),
  created_at: z.date(),
  updated_at: z.date(),
  source: ZodB2bCompanyDocumentSource,
  co_sepa_mandate_id: ZodId.nullable().optional(),
});

export type B2bCompanyDocumentDetails = z.TypeOf<typeof ZodB2bCompanyDocumentDetails>;

export const ZodGetB2bCompanyDocumentsResponse = z.array(ZodB2bCompanyDocumentDetails);

export type GetB2bCompanyDocumentsResponse = z.TypeOf<typeof ZodGetB2bCompanyDocumentsResponse>;

export const getB2bCompanyDocumentsSortColumns: (keyof B2bCompanyDocumentDetails)[] = [
  'id',
  'company_id',
  'created_at',
];

const ZodB2bCompanyDocumentsFilters = z
  .object({
    ...ZodB2bCompanyDocumentDetails.pick({
      id: true,
      co_sepa_mandate_id: true,
      task_id: true,
      is_archived: true,
      b2b_campaign_id: true,
      savings_funds_request_id: true,
    }).shape,
    from_created_at: ZodTrimmedString,
    to_created_at: ZodTrimmedString,
    type: ZodB2bCompanyDocumentType.or(z.array(ZodB2bCompanyDocumentType)),
  })
  .partial()
  .merge(
    ZodB2bCompanyDocumentDetails.pick({
      company_id: true,
    }),
  );

export type B2bCompanyDocumentsFilters = z.TypeOf<typeof ZodB2bCompanyDocumentsFilters>;

export const ZodB2bCompanyDocumentsQuery = buildZodBoListQueryParsed(
  ZodB2bCompanyDocumentsFilters,
  getB2bCompanyDocumentsSortColumns,
  {
    range: [0, 100],
    filter: { company_id: 0 },
  },
);

export type B2bCompanyDocumentsQuery = BoListQueryBase<
  B2bCompanyDocumentsFilters,
  (typeof getB2bCompanyDocumentsSortColumns)[number]
>;

export const getB2bCompaniesDocumentsEndpoint = endpoint({
  method: 'GET',
  queryParams: ZodB2bCompanyDocumentsQuery,
  responseBody: ZodGetB2bCompanyDocumentsResponse,
});

export type GetB2bCompaniesDocumentsEndpoint = GetEndpointType<
  typeof getB2bCompaniesDocumentsEndpoint
>;

export const getB2bCompaniesDocumentsIdEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodIDPathParam,
  responseBody: ZodB2bCompanyDocumentDetails,
});

export type GetB2bCompaniesDocumentsIdEndpoint = GetEndpointType<
  typeof getB2bCompaniesDocumentsIdEndpoint
>;

export const ZodPutCompanyDocumentsIdParams = createZodParamsValidator(
  z.object({
    id: ZodId,
  }),
);

export const ZodPutCompanyDocumentsIdRequestBody = z.object({
  name: ZodTrimmedString,
});

export const ZodPutCompanyDocumentsIdResponseBody = z.object({
  id: ZodId,
  name: ZodTrimmedString,
});

export const ZodSiret = ZodTrimmedString.length(14);

const ZodB2bCompanyLogo = ZodFileUploadInput.nullable();

const ZodCollectiveAgreementCode = z
  .string()
  .regex(/^\d{4}$/)
  .nullable();

export const ZodPostCompanyContact = z.object({
  id: ZodId.nullable().optional(),
  firstname: ZodTrimmedString,
  lastname: ZodTrimmedString,
  email: ZodSecureEmail,
  landline: ZodPhoneNumber.nullable().optional(),
  phone_number: ZodPhoneNumber.nullable().optional(),
  role: ZodTrimmedString,
  is_migrated: z.boolean().nullable().optional(),
});

export type PostCompanyContact = z.infer<typeof ZodPostCompanyContact>;

export const ZodPostLegalRepresentative = ZodPostCompanyContact.merge(
  z.object({
    is_business_correspondent: z.boolean(),
  }),
);

export type PostLegalRepresentative = z.TypeOf<typeof ZodPostLegalRepresentative>;

// Zod forbids `.pick()`/`.omit()`/`.partial()` on refined objects, so derive from this one
const ZodPostB2bCompanyBodyUnrefined = z.object({
  name: ZodTrimmedString,
  siret: ZodSiret.nullable().optional(),
  international_identifier: ZodTrimmedString.nullable().optional(),
  registration_date: ZodDate.nullable().optional(),
  logo: ZodB2bCompanyLogo.optional(),
  start_fiscal_year: ZodYearlyDate,
  naf_code: ZodTrimmedString.nullable().optional(),
  legal_form: ZodB2bCompanyLegalForm.nullable(),
  rcs_location: ZodTrimmedString.nullable().optional(),
  headcount: z.number().int().nonnegative().nullable().optional(),
  previous_naf_code: ZodTrimmedString.nullable().optional(),
  collective_agreement_code: ZodCollectiveAgreementCode.optional(),
  collective_agreement_name: ZodTrimmedString.nullable().optional(),
  domains: ZodDomain.array().optional(),
  parent_company_id: ZodId.nullable().optional(),
  street: ZodTrimmedString.nullish(),
  postalcode: ZodTrimmedString.nullish(),
  city: ZodTrimmedString.nullish(),
  country: ZodCountryCode,
  legal_representatives: z.array(ZodPostLegalRepresentative),
  business_correspondents: z.array(ZodPostCompanyContact),
  mailing_street: ZodTrimmedString.nullable().optional(),
  mailing_postalcode: ZodTrimmedString.nullable().optional(),
  mailing_city: ZodTrimmedString.nullable().optional(),
  mailing_country: ZodCountryCode.nullable().optional(),
  billing_street: ZodTrimmedString.nullable().optional(),
  billing_postalcode: ZodTrimmedString.nullable().optional(),
  billing_city: ZodTrimmedString.nullable().optional(),
  billing_country: ZodCountryCode.nullable().optional(),
  status: ZodB2bCompanyStatus.optional(),
  inactive_reason: ZodB2bCompanyInactiveReason.nullable().optional(),
  flags: ZodB2bCompaniesFlags,
  retirement_age: z.number().int().min(55).max(70).nullable(),
  is_confidential: z.boolean().optional(),
  // Properties below are populated only by API, not from BO UI
  is_migrated: z.boolean().optional(),
  id_external: ZodTrimmedString.nullable().optional(),
  id_external_taiga: z.number().int().optional(),
  external_code_enterprise: z.number().int().optional(),
  is_npai: z.boolean().nullable().optional(),
  is_npai_mailing: z.boolean().nullable().optional(),
  is_npai_billing: z.boolean().nullable().optional(),
  is_international: z.boolean().nullable().optional(),
});

export const ZodPostB2bCompanyBody = ZodPostB2bCompanyBodyUnrefined.refine(
  (data) =>
    data.country === 'FR'
      ? G.isNotNullable(data.siret) && G.isNullable(data.international_identifier)
      : G.isNotNullable(data.international_identifier) && G.isNullable(data.siret),
  'If country is FR, siret must be set, otherwise international_identifier must be set',
).refine(
  (data) =>
    !(
      !data.is_npai &&
      (G.isNullable(data.street) || G.isNullable(data.postalcode) || G.isNullable(data.city))
    ),
  { message: 'Either set all address fields or set is_npai to true' },
);

export type PostB2bCompanyBody = PostB2bCompaniesEndpoint['Client']['RequestBody'];

export const ZodPutB2bCompanyBody = z.object({
  ...ZodPostB2bCompanyBodyUnrefined.omit({
    is_migrated: true,
    id_external: true,
    logo: true,
    is_npai: true,
    is_npai_billing: true,
    is_npai_mailing: true,
  }).shape,
  logo_url: ZodTrimmedString.nullable().optional(),
  kyb_status: ZodKybStatus,
});

export type PutB2bCompanyBody = z.infer<typeof ZodPutB2bCompanyBody>;

export const getB2bCompaniesIdEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodIDPathParam,
  responseBody: ZodB2bCompanyDetails,
});

export type GetB2bCompaniesIdEndpoint = GetEndpointType<typeof getB2bCompaniesIdEndpoint>;

export const B2bCompanyEditFieldValues = [
  'name',
  'siret',
  'registration_date',
  'logo',
  'naf_code',
  'legal_form',
  'rcs_location',
  'headcount',
  'previous_naf_code',
  'collective_agreement_code',
  'collective_agreement_name',
  'domains',
  'parent_company_id',
  'street',
  'postalcode',
  'city',
  'country',
  'legal_representatives',
  'business_correspondents',
  'mailing_street',
  'mailing_postalcode',
  'mailing_city',
  'mailing_country',
  'billing_street',
  'billing_postalcode',
  'billing_city',
  'billing_country',
  'status',
  'inactive_reason',
  'flags',
  'retirement_age',
  'logo_url',
  'kyb_status',
  'start_fiscal_year',
  'is_confidential',
] as const;

const ZodB2bCompanyEditField = z.enum(B2bCompanyEditFieldValues);

export type B2bCompanyEditField = z.infer<typeof ZodB2bCompanyEditField>;

export const ZodEditB2bCompanyDataRequestOne = z.discriminatedUnion('field', [
  z.object({
    field: z.enum(['name', 'street', 'postalcode', 'city']),
    to: ZodTrimmedString,
  }),
  z.object({
    field: z.literal('legal_form'),
    to: ZodB2bCompanyLegalForm,
  }),
  z.object({
    field: z.enum([
      'naf_code',
      'rcs_location',
      'previous_naf_code',
      'collective_agreement_name',
      'mailing_street',
      'mailing_postalcode',
      'mailing_city',
      'billing_street',
      'billing_postalcode',
      'billing_city',
      'logo_url',
    ]),
    to: ZodTrimmedString.nullable(),
  }),
  z.object({
    field: z.literal('siret'),
    to: ZodSiret,
  }),
  z.object({
    field: z.literal('country'),
    to: ZodCountryCode,
  }),
  z.object({
    field: z.enum(['mailing_country', 'billing_country']),
    to: ZodCountryCode.nullable(),
  }),
  z.object({
    field: z.literal('retirement_age'),
    to: z.number().int().min(55).max(70).nullable(),
  }),
  z.object({
    field: z.literal('flags'),
    to: ZodB2bCompaniesFlags,
  }),
  z.object({
    field: z.literal('inactive_reason'),
    to: ZodB2bCompanyInactiveReason.nullable(),
  }),
  z.object({
    field: z.literal('status'),
    to: ZodB2bCompanyStatus,
  }),
  z.object({
    field: z.literal('legal_representatives'),
    to: ZodPostLegalRepresentative.array(),
  }),
  z.object({
    field: z.literal('business_correspondents'),
    to: ZodPostCompanyContact.array(),
  }),
  z.object({
    field: z.literal('parent_company_id'),
    to: ZodId.nullable(),
  }),
  z.object({
    field: z.literal('collective_agreement_code'),
    to: ZodCollectiveAgreementCode,
  }),
  z.object({
    field: z.literal('headcount'),
    to: z.number().int().nonnegative().nullable(),
  }),
  z.object({
    field: z.literal('registration_date'),
    to: ZodDate.nullable(),
  }),
  z.object({
    field: z.literal('start_fiscal_year'),
    to: ZodYearlyDate,
  }),
  z.object({
    field: z.literal('kyb_status'),
    to: ZodKybStatus,
  }),
  z.object({
    field: z.literal('logo'),
    to: ZodB2bCompanyLogo.nullable(),
  }),
  z.object({
    field: z.literal('domains'),
    to: ZodDomainsList,
  }),
  z.object({
    field: z.literal('is_confidential'),
    to: z.boolean(),
  }),
]);

export type EditB2bCompanyDataRequestOne = z.infer<typeof ZodEditB2bCompanyDataRequestOne>;

const ZodEditB2bCompanyDataRequest = z.array(ZodEditB2bCompanyDataRequestOne).min(1);

export type EditB2bCompanyDataRequest = z.infer<typeof ZodEditB2bCompanyDataRequest>;

const ZodPostB2BCompanyEditRequest = z.object({
  company_id: ZodId,
  changes: ZodEditB2bCompanyDataRequest.and(z.array(z.object({ field: ZodB2bCompanyEditField }))),
  reason: ZodTrimmedString,
});

export type PostB2BCompanyEditRequest = z.infer<typeof ZodPostB2BCompanyEditRequest>;

const ZodPostB2bCompanyEditResponse = z.object({
  id: ZodId,
  state: ZodReviewedRequestState,
  created_at: z.date(),
  company_id: ZodId,
});

export const postB2bCompanyEditRequestEndpoint = endpoint({
  method: 'POST',
  body: ZodPostB2BCompanyEditRequest,
  responseBody: ZodPostB2bCompanyEditResponse,
});

export type PostB2bCompanyEditRequestEndpoint = GetEndpointType<
  typeof postB2bCompanyEditRequestEndpoint
>;

export const B2bCompanyAutoEditFieldValues = [
  'legal_representatives',
  'business_correspondents',
  'mailing_street',
  'mailing_postalcode',
  'mailing_city',
  'mailing_country',
] as const;

const ZodB2bCompanyAutoEditField = z.enum(B2bCompanyAutoEditFieldValues);

export type B2bCompanyAutoEditField = z.infer<typeof ZodB2bCompanyAutoEditField>;

const ZodAutoEditB2bCompanyDataRequestOne = z.discriminatedUnion('field', [
  z.object({
    field: z.enum(['mailing_street', 'mailing_postalcode', 'mailing_city']),
    to: ZodTrimmedString.nullable(),
  }),
  z.object({
    field: z.enum(['mailing_country']),
    to: ZodCountryCode.nullable(),
  }),
  z.object({
    field: z.literal('legal_representatives'),
    to: ZodPostLegalRepresentative.array(),
  }),
  z.object({
    field: z.literal('business_correspondents'),
    to: ZodPostCompanyContact.pick({
      phone_number: true,
      email: true,
      role: true,
      landline: true,
    }).array(),
  }),
]);

export type AutoEditB2bCompanyDataRequestOne = z.infer<typeof ZodAutoEditB2bCompanyDataRequestOne>;

const ZodAutoEditB2bCompanyDataRequest = z.array(ZodAutoEditB2bCompanyDataRequestOne).min(1);

export type AutoEditB2bCompanyDataRequest = z.infer<typeof ZodAutoEditB2bCompanyDataRequest>;

const ZodPostB2BCompanyAutoEditRequest = z.object({
  company_id: ZodId,
  changes: ZodAutoEditB2bCompanyDataRequest.and(
    z.array(z.object({ field: ZodB2bCompanyAutoEditField })),
  ),
  reason: ZodTrimmedString,
});

export type PostB2BCompanyAutoEditRequest = z.infer<typeof ZodPostB2BCompanyAutoEditRequest>;

export const postB2bCompanyAutoEditRequestEndpoint = endpoint({
  method: 'POST',
  body: ZodPostB2BCompanyAutoEditRequest,
  responseBody: ZodPostB2bCompanyEditResponse,
});

export type PostB2bCompanyAutoEditRequestEndpoint = GetEndpointType<
  typeof postB2bCompanyAutoEditRequestEndpoint
>;

const ZodB2bEditRequestsAction = z.enum(['APPROVED', 'REJECTED']);

const ZodPostB2bEditRequestsActionBody = z.object({
  review_request_id: ZodId,
  state: ZodB2bEditRequestsAction,
});

export type PostB2bEditRequestsActionBody = z.infer<typeof ZodPostB2bEditRequestsActionBody>;

const ZodPostB2bEditRequestsActionResponse = z.object({
  id: ZodId,
  company_id: ZodId,
  requester_id: ZodId,
  requester_email: ZodTrimmedString.nullable(),
  reason: ZodTrimmedString,
  state: z.enum(['REQUESTED', 'APPROVED', 'REJECTED']),
  created_at: z.date(),
  updated_at: z.date(),
  reviewer_id: ZodId.nullable(),
  reviewer_email: ZodTrimmedString.nullable(),
  reviewed_at: z.date().nullable(),
  type: ZodTrimmedString,
  error_message: ZodTrimmedString.nullable(),
  assignee_id: ZodId.nullable(),
  assignee_email: ZodTrimmedString.nullable(),
});

export const postB2bCompaniesEditRequestsActionEndpoint = endpoint({
  method: 'POST',
  body: ZodPostB2bEditRequestsActionBody,
  responseBody: ZodPostB2bEditRequestsActionResponse,
});

export type PostB2bCompaniesEditRequestsActionEndpoint = GetEndpointType<
  typeof postB2bCompaniesEditRequestsActionEndpoint
>;

export const postB2bCompaniesEndpoint = endpoint({
  method: 'POST',
  body: ZodPostB2bCompanyBody,
  responseBody: ZodB2bCompanyDetails,
});

export type PostB2bCompaniesEndpoint = GetEndpointType<typeof postB2bCompaniesEndpoint>;

export const getB2bCompaniesEndpoint = endpoint({
  method: 'GET',
  queryParams: ZodGetB2bCompaniesQuery,
  responseBody: ZodGetB2bCompaniesResponse,
});

export type GetB2bCompaniesEndpoint = GetEndpointType<typeof getB2bCompaniesEndpoint>;

export const postB2bCompaniesDocumentsEndpoint = endpoint({
  method: 'POST',
  body: ZodB2bPostCompanyDocumentRequest,
  responseBody: ZodB2bCompanyDocumentDetails,
});

export type PostB2bCompaniesDocumentsEndpoint = GetEndpointType<
  typeof postB2bCompaniesDocumentsEndpoint
>;

export const putCompanyDocumentsIdEndpoint = endpoint({
  method: 'PUT',
  pathParams: ZodPutCompanyDocumentsIdParams,
  body: ZodPutCompanyDocumentsIdRequestBody,
  responseBody: ZodPutCompanyDocumentsIdResponseBody,
});

export type PutCompanyDocumentsIdEndpoint = GetEndpointType<typeof putCompanyDocumentsIdEndpoint>;

export const ZodB2bDashboardMatchingContributionsYear = z.object({
  totalPotentialAmount: z.number(),
  alreadyPaid: z.number(),
  awaitingCompany: z.number(),
  notDistributed: z.number(),
});

export const ZodB2bDashboardMatchingContributionsData = z.record(
  ZodTrimmedString,
  ZodB2bDashboardMatchingContributionsYear,
);

export const ZodB2bDashboardMatchingContributionsResponse = z.object({
  id: ZodId,
  data: ZodB2bDashboardMatchingContributionsData,
});

export const GetB2bDashboardMatchingContributionsEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodIDPathParam,
  responseBody: ZodB2bDashboardMatchingContributionsResponse,
});

export type GetB2bDashboardMatchingContributionsResponse = z.infer<
  typeof ZodB2bDashboardMatchingContributionsResponse
>;

export type GetB2bDashboardMatchingContributionsData = z.infer<
  typeof ZodB2bDashboardMatchingContributionsData
>;

const activeCampaign = z.object({
  campaign_id: ZodId,
  campaign_type: ZodTrimmedString,
  status: ZodTrimmedString,
  amount: z.number(),
  answer_rate: z.number(),
  days_remaining: z.number(),
  temporal_progress: z.number(),
  start_date: z.date(),
  end_date: z.date(),
});

const ZodB2bDashboardActiveCampaignsData = z.array(activeCampaign);

export type GetB2bDashboardActiveCampaignsData = z.infer<typeof ZodB2bDashboardActiveCampaignsData>;

const ZodB2bDashboardActiveCampaignsResponse = z.object({
  id: ZodId,
  data: ZodB2bDashboardActiveCampaignsData,
});

export const getB2bDashboardActiveCampaignsEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodIDPathParam,
  responseBody: ZodB2bDashboardActiveCampaignsResponse,
});

export type GetB2bDashboardActiveCampaignsEndpoint = GetEndpointType<
  typeof getB2bDashboardActiveCampaignsEndpoint
>;

export type GetB2bDashboardActiveCampaignsResponse = z.infer<
  typeof ZodB2bDashboardActiveCampaignsResponse
>;

export const getCompaniesSearchEndpoint = endpoint({
  method: 'GET',
  queryParams: buildZodBoListQueryParsed(
    z.object({
      search: ZodTrimmedString.min(3),
    }),
    ['name'],
    { range: [0, 100] },
  ),
  responseBody: z.array(z.object({ ...ZodSearchCompanyItem.shape, id: ZodId })),
});

export type GetCompaniesSearchEndpoint = GetEndpointType<typeof getCompaniesSearchEndpoint>;

export const ZodGetCompaniesSiretDirectorsParams = buildZodBoListQueryParsed(
  z.object({
    siret: ZodTrimmedString,
  }),
  ['first_name'],
  { range: [0, 100] },
);

export const getCompaniesSiretDirectorsEndpoint = endpoint({
  method: 'GET',
  queryParams: ZodGetCompaniesSiretDirectorsParams,
  responseBody: z.array(
    z.object({ ...ZodCompanyDirector.shape, birth_date: ZodTrimmedString.nullable() }),
  ),
});

export type GetCompaniesSiretDirectorsEndpoint = GetEndpointType<
  typeof getCompaniesSiretDirectorsEndpoint
>;

export const paymentMethodIntentValues = ['principal', 'invoicing', 'ccb_withdrawal'] as const;
const sepaPaymentTypeValues = ['recurring', 'one_off'] as const;
export const paymentMethodTypeValues = ['sepa_direct_debit', 'external_bank'] as const;
const ZodPaymentMethodIntent = z.enum(paymentMethodIntentValues);
export type PaymentMethodIntent = z.infer<typeof ZodPaymentMethodIntent>;
const ZodPaymentMethodPaymentType = z.enum(sepaPaymentTypeValues);
export type PaymentMethodPaymentType = z.infer<typeof ZodPaymentMethodPaymentType>;

const ZodPaymentMethodStatus = z.enum([
  'active',
  'deleted',
  'rejected',
  'pending',
  'awaiting_customer',
  'awaiting_signature',
]);
export type PaymentMethodStatus = z.infer<typeof ZodPaymentMethodStatus>;

const ZodPaymentMethodType = z.enum(paymentMethodTypeValues);
export type PaymentMethodType = z.infer<typeof ZodPaymentMethodType>;

export const ZodB2bCompanyPaymentMethod = z.object({
  id: ZodId,
  b2b_company_id: ZodId,
  type: ZodPaymentMethodType,
  intent: ZodPaymentMethodIntent,
  co_sepa_mandate_id: ZodId.nullable(),
  mdlr_beneficiary_id: ZodId.nullable(),
  bank_name: ZodTrimmedString.nullable(),
  beneficiary_name: ZodTrimmedString.nullable(),
  status: ZodPaymentMethodStatus,
  iban: ZodTrimmedString,
  payment_type: ZodPaymentMethodPaymentType,
  mandate_unique_reference: ZodTrimmedString.nullable(),
  mandate_singed_at: z.date().nullable(),
  created_at: z.date(),
  updated_at: z.date(),
  mandate_signature_document_name: ZodTrimmedString.nullable(),
  mandate_signature_document_url: ZodTrimmedString.nullable(),
  retry_reason: ZodTrimmedString.nullable(),
  retry_comment: ZodTrimmedString.nullable(),
});

export type B2bCompanyPaymentMethod = z.infer<typeof ZodB2bCompanyPaymentMethod>;

export const ZodGetB2bCompanyPaymentMethodResponse = z.array(ZodB2bCompanyPaymentMethod);

export type GetB2bCompanyPaymentMethodResponse = z.infer<
  typeof ZodGetB2bCompanyPaymentMethodResponse
>;

export const ZodB2bCompanyPaymentMethodQuery = buildZodBoListQueryParsed(
  z
    .object({
      b2b_company_id: ZodId,
      from_created_at: ZodTrimmedString,
      to_created_at: ZodTrimmedString,
    })
    .partial(),
  ['id'],
  {
    range: [0, 100],
    filter: { b2b_company_id: 0 },
  },
);

export type B2bCompanyPaymentMethodQuery = z.infer<typeof ZodB2bCompanyPaymentMethodQuery>;

export const getB2bCompaniesPaymentMethodsEndpoint = endpoint({
  method: 'GET',
  queryParams: ZodB2bCompanyPaymentMethodQuery,
  responseBody: ZodGetB2bCompanyPaymentMethodResponse,
});

export type GetB2bCompaniesPaymentMethodsEndpoint = GetEndpointType<
  typeof getB2bCompaniesPaymentMethodsEndpoint
>;

export const ZodB2bSepaMandateExternalSignatory = z.object({
  firstname: ZodTrimmedString.min(1).max(100),
  lastname: ZodTrimmedString.min(1).max(100),
  email: z.email(),
});

export type B2bSepaMandateExternalSignatory = z.infer<typeof ZodB2bSepaMandateExternalSignatory>;

const SepaMandateDetails = z.object({
  type: z.literal('sepa_direct_debit').optional().default('sepa_direct_debit'),
  company_id: ZodId,
  iban: ZodBankAccountIban,
  bic: ZodTrimmedString,
  payment_type: z.enum(sepaPaymentTypeValues),
  intent: ZodPaymentMethodIntent,
  presigned_path: ZodTrimmedString.nullable(),
  document_name: ZodTrimmedString.nullable(),
  is_sepa_mandate_signed: z.boolean().optional(),
  external_signatory: ZodB2bSepaMandateExternalSignatory.optional(),
});

export type SepaMandateDetails = z.infer<typeof SepaMandateDetails>;

const ZodExternalBankDetailsBase = z.object({
  type: z.literal('external_bank'),
  company_id: ZodId,
  intent: ZodPaymentMethodIntent,
  presigned_path: ZodTrimmedString.nullable(),
  document_name: ZodTrimmedString.nullable(),
});

const ZodExternalBankDetailsIban = z.object({
  account_type: z.literal('iban'),
  iban: ZodBankAccountIban,
  bic: ZodBankAccountBic,
});

const ZodExternalBankDetailsIntl = z.object({
  account_type: z.literal('intl'),
  account_number: ZodIntlAccountNumber,
  bic: ZodNonSepaIntlBic,
});

const ZodExternalBankDetailsScan = z.object({
  account_type: z.literal('scan'),
  sort_code: ZodUkSortCode,
  account_number: ZodUkAccountNumber,
  bic: ZodBankAccountBic,
});

export const ZodExternalBankDetails = z.intersection(
  ZodExternalBankDetailsBase,
  z.discriminatedUnion('account_type', [
    ZodExternalBankDetailsIban,
    ZodExternalBankDetailsIntl,
    ZodExternalBankDetailsScan,
  ]),
);

export type ExternalBankDetails = z.infer<typeof ZodExternalBankDetails>;
export type ExternalBankAccountType = ExternalBankDetails['account_type'];

const PaymentMethodDetails = z.union([SepaMandateDetails, ZodExternalBankDetails]);

export type PaymentMethodDetails = z.infer<typeof PaymentMethodDetails>;

const ZodPostB2bCompaniesPaymentMethodBody = PaymentMethodDetails;

export type PostB2bCompaniesPaymentMethodBody = z.infer<
  typeof ZodPostB2bCompaniesPaymentMethodBody
>;

export const postB2bCompaniesPaymentMethodEndpoint = endpoint({
  method: 'POST',
  body: ZodPostB2bCompaniesPaymentMethodBody,
  responseBody: z.object({
    id: ZodId,
  }),
});

export type PostB2bCompaniesPaymentMethodEndpoint = GetEndpointType<
  typeof postB2bCompaniesPaymentMethodEndpoint
>;

export const ZodB2bCompanyInstrument = z.object({
  id: ZodId,
  name: ZodTrimmedString,
});
export type B2bCompanyInstrument = z.TypeOf<typeof ZodB2bCompanyInstrument>;
export const ZodB2bCompanyInstrumentsResponse = z.object({
  id: ZodId,
  pei_instruments: z.array(ZodB2bCompanyInstrument),
  perecoi_instruments: z.array(ZodB2bCompanyInstrument),
});

export type B2bCompanyInstrumentsResponse = z.TypeOf<typeof ZodB2bCompanyInstrumentsResponse>;

export const getB2bCompaniesInstrumentsEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodIDPathParam,
  responseBody: ZodB2bCompanyInstrumentsResponse,
});

export type GetB2bCompaniesInstrumentsEndpoint = GetEndpointType<
  typeof getB2bCompaniesInstrumentsEndpoint
>;

const savingsProduct = z.object({
  name: ZodTrimmedString,
  amount: z.number(),
});
const savingsFund = z.object({
  name: ZodTrimmedString,
  amount: z.number(),
});

const ZodB2bDashboardSavingsData = z.object({
  total_savings: z.number(),
  per_product: z.array(savingsProduct),
  per_fund: z.array(savingsFund),
});

export type GetB2bDashboardSavingsData = z.infer<typeof ZodB2bDashboardSavingsData>;

export const ZodB2bDashboardSavingsResponse = z.object({
  id: ZodId,
  data: ZodB2bDashboardSavingsData,
});

export const getB2bDashboardSavingsEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodIDPathParam,
  responseBody: ZodB2bDashboardSavingsResponse,
});

export type GetB2bDashboardSavingsEndpoint = GetEndpointType<typeof getB2bDashboardSavingsEndpoint>;

export type GetB2bDashboardSavingsResponse = z.infer<
  (typeof getB2bDashboardSavingsEndpoint)['responseBody']
>;

export const getB2bDashboardMatchingContributionsEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodIDPathParam,
  responseBody: ZodB2bDashboardMatchingContributionsResponse,
});

export type GetB2bDashboardMatchingContributionsEndpoint = GetEndpointType<
  typeof getB2bDashboardMatchingContributionsEndpoint
>;

const ZodB2bDashboardAffiliatesData = z.object({
  affiliate_arbitrages: z.number(),
  affiliate_volontary_deposits: z.number(),
});

export type GetB2bDashboardAffiliatesData = z.infer<typeof ZodB2bDashboardAffiliatesData>;

const ZodB2bDashboardAffiliatesResponse = z.object({
  id: ZodId,
  data: ZodB2bDashboardAffiliatesData,
});

export const getB2bDashboardAffiliatesEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodIDPathParam,
  responseBody: ZodB2bDashboardAffiliatesResponse,
});

export type GetB2bDashboardAffiliatesEndpoint = GetEndpointType<
  typeof getB2bDashboardAffiliatesEndpoint
>;

export type GetB2bDashboardAffiliatesResponse = z.infer<
  (typeof getB2bDashboardAffiliatesEndpoint)['responseBody']
>;

export const ZodGetB2bCompanyBalanceResponse = z.object({
  id: ZodId,
  currency: ZodCurrency,
  available_balance: z.number().int(),
});

export type GetB2bCompanyBalanceResponse = z.TypeOf<typeof ZodGetB2bCompanyBalanceResponse>;

export const getB2bCompanyBalanceEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodIDPathParam,
  responseBody: ZodGetB2bCompanyBalanceResponse,
});

export type GetB2bCompanyBalanceEndpoint = GetEndpointType<typeof getB2bCompanyBalanceEndpoint>;
